import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { PhoneIcon, PhoneXMarkIcon, MicrophoneIcon } from "@heroicons/react/24/solid";
import api from "../../services/api";
import { getLiveSocket, holdLiveSocket } from "../../services/binSocket";
import { useAuth } from "../../contexts/AuthContext";
import MeetModal from "./MeetModal";
import { telHref } from "./phone";

const LiveContext = createContext(null);

export function useLive() {
  return useContext(LiveContext);
}

function beep(kind) {
  try {
    const audio = new AudioContext();
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.frequency.value = kind === "waiting" ? 880 : 440;
    gain.gain.value = 0.05;
    oscillator.connect(gain);
    gain.connect(audio.destination);
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audio.close();
    }, kind === "waiting" ? 180 : 400);
  } catch (error) {
    // Autoplay can block the tone until the next tap.
  }
}

let ringAudio = null;
let ringWanted = false;

function ringElement() {
  if (!ringAudio) {
    ringAudio = new Audio(`${process.env.PUBLIC_URL || ""}/sounds/ring.wav`);
    ringAudio.loop = true;
    ringAudio.volume = 0.7;
  }
  return ringAudio;
}

function startRing() {
  ringWanted = true;
  const audio = ringElement();
  audio.play().catch(() => {
    const retry = () => {
      if (ringWanted) audio.play().catch(() => {});
    };
    window.addEventListener("pointerdown", retry, { once: true });
  });
}

function stopRing() {
  ringWanted = false;
  if (!ringAudio) return;
  ringAudio.pause();
  ringAudio.currentTime = 0;
}

export default function LiveProvider({ children }) {
  const { user } = useAuth();
  const [call, setCall] = useState(null);
  const [waiting, setWaiting] = useState(null);
  const [meetBinId, setMeetBinId] = useState(null);
  const [muted, setMuted] = useState(false);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const callRef = useRef(null);
  const acceptingRef = useRef(false);
  callRef.current = call;

  const cleanupMedia = () => {
    localRef.current?.getTracks?.().forEach((track) => track.stop());
    localRef.current = null;
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (remoteRef.current) remoteRef.current.srcObject = null;
  };

  const ensurePeer = async (iceServers) => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({ iceServers: iceServers || [{ urls: "stun:stun.l.google.com:19302" }] });
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localRef.current = stream;
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
    pc.ontrack = (event) => {
      if (remoteRef.current) remoteRef.current.srcObject = event.streams[0];
    };
    pc.onicecandidate = (event) => {
      const current = callRef.current;
      if (!event.candidate || !current?.callId) return;
      getLiveSocket()?.emit("call:signal", { callId: current.callId, kind: "ice", data: event.candidate });
    };
    pcRef.current = pc;
    return pc;
  };

  useEffect(() => {
    if (!user) return undefined;
    const release = holdLiveSocket();
    const socket = getLiveSocket();
    if (!socket) {
      release();
      return undefined;
    }
    const ping = setInterval(() => {
      if (socket.connected) socket.emit("presence:ping");
    }, 45000);
    if (socket.connected) socket.emit("presence:ping");

    const onIncoming = (payload) => {
      const myId = String(user?._id || user?.id || "");
      if (payload.from && myId && String(payload.from) === myId) {
        getLiveSocket()?.emit("call:end", { callId: payload.callId });
        setCall({
          phase: "self",
          message: "You can't call yourself. You are the seller and the agent on this order.",
        });
        return;
      }
      startRing();
      setCall({
        phase: "incoming",
        callId: payload.callId,
        binId: payload.binId,
        name: payload.fromName,
        iceServers: payload.iceServers,
        role: "callee",
      });
    };
    const onAccepted = async (payload) => {
      const current = callRef.current;
      if (!current || (current.callId && current.callId !== payload.callId && !acceptingRef.current)) return;
      const answeredElsewhere =
        current.role === "callee" &&
        ((payload.answeredSocketId && payload.answeredSocketId !== socket.id) ||
          (!payload.answeredSocketId && !acceptingRef.current));
      if (answeredElsewhere) {
        acceptingRef.current = false;
        stopRing();
        cleanupMedia();
        setCall(null);
        setMuted(false);
        return;
      }
      acceptingRef.current = false;
      stopRing();
      const next = {
        ...current,
        phase: "active",
        callId: payload.callId,
        iceServers: payload.iceServers || current.iceServers,
        role: current.role || "caller",
      };
      setCall(next);
      callRef.current = next;
      try {
        const pc = await ensurePeer(next.iceServers);
        if (next.role !== "callee") {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit("call:signal", { callId: next.callId, kind: "offer", data: offer });
        }
      } catch (error) {
        setCall({ phase: "inactive", message: "Allow the microphone to place this call." });
      }
    };
    const onSignal = async ({ callId, kind, data }) => {
      const current = callRef.current;
      if (!current || current.callId !== callId) return;
      const pc = await ensurePeer(current.iceServers);
      if (kind === "offer") {
        await pc.setRemoteDescription(data);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("call:signal", { callId, kind: "answer", data: answer });
      } else if (kind === "answer") {
        await pc.setRemoteDescription(data);
      } else if (kind === "ice" && data) {
        await pc.addIceCandidate(data).catch(() => {});
      }
    };
    const onEnded = () => {
      if (acceptingRef.current) return;
      stopRing();
      cleanupMedia();
      setCall(null);
      setWaiting(null);
      setMuted(false);
    };
    const onWaiting = (payload) => {
      beep("waiting");
      setWaiting(payload);
    };

    socket.on("call:incoming", onIncoming);
    socket.on("call:accepted", onAccepted);
    socket.on("call:signal", onSignal);
    socket.on("call:ended", onEnded);
    socket.on("call:waiting", onWaiting);

    return () => {
      clearInterval(ping);
      release();
      socket.off("call:incoming", onIncoming);
      socket.off("call:accepted", onAccepted);
      socket.off("call:signal", onSignal);
      socket.off("call:ended", onEnded);
      socket.off("call:waiting", onWaiting);
    };
  }, [user]);

  const startCall = async (binId, peerId) => {
    const myId = String(user?._id || user?.id || "");
    if (peerId && myId && String(peerId) === myId) {
      setCall({
        phase: "self",
        message: "You can't call yourself. You are the seller and the agent on this order.",
      });
      return;
    }
    const primed = ringElement();
    primed.play().then(() => {
      if (!ringWanted) {
        primed.pause();
        primed.currentTime = 0;
      }
    }).catch(() => {});
    const response = await api.post("/calls/start", { binId });
    const data = response.data.data;
    if (data.mode === "self" || (data.peer?.id && myId && String(data.peer.id) === myId)) {
      if (data.callId) getLiveSocket()?.emit("call:end", { callId: data.callId });
      setCall({
        phase: "self",
        message: data.message || "You can't call yourself. You are the seller and the agent on this order.",
      });
      return;
    }
    if (data.mode === "dial") {
      window.location.href = telHref(data.phoneNumber);
      return;
    }
    if (data.mode === "inactive" || data.mode === "busy" || data.mode === "busy-self") {
      setCall({
        phase: data.mode === "busy" ? "busy" : "inactive",
        name: data.peer?.name,
        message: data.message,
        phoneNumber: data.peer?.phoneNumber,
        callId: null,
      });
      return;
    }
    setCall({
      phase: "ringing",
      callId: data.callId,
      binId,
      name: data.peer?.name,
      iceServers: data.iceServers,
      role: "caller",
    });
    startRing();
  };

  const endCall = () => {
    acceptingRef.current = false;
    stopRing();
    const current = callRef.current;
    if (current?.callId) getLiveSocket()?.emit("call:end", { callId: current.callId });
    cleanupMedia();
    setCall(null);
    setMuted(false);
  };

  const value = {
    startCall,
    openMeet: (binId) => setMeetBinId(binId),
  };

  return (
    <LiveContext.Provider value={value}>
      {children}
      <audio ref={remoteRef} autoPlay />
      {call && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <div className="w-full max-w-sm rounded-3xl bg-gray-950 p-6 text-white shadow-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-300">
              {call.phase === "self" ? "Jhonks" : "Jhonks call"}
            </p>
            <h2 className="mt-3 text-2xl font-bold">{call.phase === "self" ? "Same account" : call.name || "Jhonks"}</h2>
            <p className="mt-2 text-sm text-gray-300">
              {call.phase === "ringing" && "Calling…"}
              {call.phase === "incoming" && "Incoming audio call"}
              {call.phase === "active" && "Connected"}
              {call.phase === "busy" && (call.message || "On another call")}
              {call.phase === "inactive" && (call.message || "This person is not active.")}
              {call.phase === "self" && (call.message || "You can't call yourself. You are the seller and the agent on this order.")}
            </p>
            {waiting && call.phase === "active" && (
              <div className="mt-4 rounded-2xl bg-amber-500/20 p-3 text-sm">
                <p className="font-semibold">{waiting.fromName} is calling.</p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-900"
                    onClick={() => setWaiting(null)}
                  >
                    Stay on this call
                  </button>
                  <button
                    type="button"
                    className="rounded-full bg-green-500 px-3 py-1.5 text-xs font-semibold text-white"
                    onClick={() => {
                      acceptingRef.current = true;
                      cleanupMedia();
                      const next = { phase: "active", name: waiting.fromName, role: "callee" };
                      callRef.current = next;
                      setCall(next);
                      getLiveSocket()?.emit("call:take-waiting");
                      setWaiting(null);
                    }}
                  >
                    End and answer
                  </button>
                </div>
              </div>
            )}
            {call.phoneNumber && call.phase !== "active" && call.phase !== "incoming" && call.phase !== "ringing" && (
              <a href={telHref(call.phoneNumber)} className="mt-4 inline-block text-sm font-semibold text-green-300">
                Call their phone instead
              </a>
            )}
            <div className="mt-6 flex items-center justify-center gap-3">
              {call.phase === "self" && (
                <button
                  type="button"
                  className="rounded-full bg-green-500 px-5 py-2.5 text-sm font-semibold text-white"
                  onClick={endCall}
                >
                  OK
                </button>
              )}
              {call.phase === "incoming" && (
                <button
                  type="button"
                  className="rounded-full bg-green-500 p-4"
                  onClick={() => {
                    acceptingRef.current = true;
                    getLiveSocket()?.emit("call:accept", { callId: call.callId });
                  }}
                >
                  <PhoneIcon className="h-6 w-6" />
                </button>
              )}
              {call.phase === "active" && (
                <button
                  type="button"
                  className="rounded-full bg-white/10 p-4"
                  onClick={() => {
                    const next = !muted;
                    setMuted(next);
                    localRef.current?.getAudioTracks?.().forEach((track) => {
                      track.enabled = !next;
                    });
                  }}
                >
                  <MicrophoneIcon className="h-6 w-6" />
                </button>
              )}
              {call.phase !== "self" && (
                <button type="button" className="rounded-full bg-red-500 p-4" onClick={endCall}>
                  <PhoneXMarkIcon className="h-6 w-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {meetBinId && <MeetModal binId={meetBinId} onClose={() => setMeetBinId(null)} />}
    </LiveContext.Provider>
  );
}
